// src/components/DensityToggle.tsx
// Segmented control for switching between compact / normal / relaxed density modes.

import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { cn } from '@core/ui';

import type { DensityMode } from '@/features/workspace/types';

// ---------------------------------------------------------------------------
// Inline SVG icons — horizontal lines at varying spacing
// ---------------------------------------------------------------------------

function CompactIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="20" y2="17" />
    </svg>
  );
}

function NormalIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="20" y2="17" />
    </svg>
  );
}

function RelaxedIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round">
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DensityToggleProps {
  density: DensityMode;
  onDensityChange: (density: DensityMode) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const DENSITY_OPTIONS: { value: DensityMode; Icon: React.FC<{ className?: string }>; labelKey: string }[] = [
  { value: 'compact', Icon: CompactIcon, labelKey: 'workspace.density.compact' },
  { value: 'normal', Icon: NormalIcon, labelKey: 'workspace.density.normal' },
  { value: 'relaxed', Icon: RelaxedIcon, labelKey: 'workspace.density.relaxed' },
];

export const DensityToggle = observer(({ density, onDensityChange }: DensityToggleProps) => {
  const { t } = useI18n();

  return (
    <div
      className="inline-flex items-center rounded-md border border-border bg-muted p-0.5"
      role="radiogroup"
      aria-label={t('workspace.density.label')}
    >
      {DENSITY_OPTIONS.map(({ value, Icon, labelKey }) => {
        const isActive = density === value;
        const label = t(labelKey);

        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={isActive}
            aria-label={label}
            title={label}
            onClick={() => onDensityChange(value)}
            className={cn(
              'inline-flex items-center justify-center rounded-sm p-1.5 transition-colors',
              isActive
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="h-4 w-4" />
          </button>
        );
      })}
    </div>
  );
});

DensityToggle.displayName = 'DensityToggle';
